import { defineStore } from 'pinia';
import { computed, ref } from 'vue';

import { createHttpClient } from '@/apis/http';
import { env } from '@/env';

const refreshTokenHttp = createHttpClient({
  baseURL: env.authUrl,
  withCredentials: true,
});

export const useTokenStore = defineStore('token', () => {
  const token = ref<string | null>(null);

  const hasAttemptedTokenRefresh = ref(false);

  const pending = ref({
    refresh: false,
  });

  const refreshPromise = ref<Promise<boolean> | null>(null);

  const isShareToken = computed(() => token.value?.startsWith('share-') ?? false);

  function setToken({ token: newToken }: { token: string }) {
    token.value = newToken;
  }

  function clearToken() {
    token.value = null;
  }

  function reset() {
    clearToken();
    hasAttemptedTokenRefresh.value = false;
    pending.value.refresh = false;
    refreshPromise.value = null;
  }

  async function refreshToken({ shouldSetToken = true } = {}): Promise<boolean> {
    pending.value.refresh = true;
    if (!refreshPromise.value) {
      refreshPromise.value = refreshTokenHttp
        .post('/api/tokens/refresh')
        .then((response) => {
          const { status, data } = response;
          if (status === 201) {
            if (shouldSetToken) {
              setToken({ token: (data as { token: string }).token });
            }
            return true;
          }
          return false;
        })
        .catch(() => false)
        .finally(() => {
          hasAttemptedTokenRefresh.value = true;
          pending.value.refresh = false;
          refreshPromise.value = null;
        });
    }
    return refreshPromise.value;
  }

  return {
    token,
    isShareToken,
    hasAttemptedTokenRefresh,
    setToken,
    clearToken,
    refreshToken,
    reset,
  };
});
